import React, { useState } from "react";
import { Alert, Box, Button, Snackbar, Stack } from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { useForm, useWatch } from "react-hook-form";

import TopBar from "../../../components/TopBar";
import SingleDatePicker from "../../../components/form/SingleDatePicker";
import VatProfileSelect, {
  getProfileVat,
} from "../../../components/form/VatProfileSelect";
import ReportTable from "../components/ReportTable";
import VATReportDisclaimerBanner from "../components/VATReportDisclaimerBanner";
import FeatureNotSupportedDialog from "../../common/FeatureNotSupportedDialog";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
import { useGetDocumentReportsHandler } from "../dataHandlers/getReportsHandler";
import { useCreateReportsJobHandler } from "../dataHandlers/createReportJobHandler";
import { reportTypes } from "../../../constants/appConstants";

import {usePermissions} from "../../../hooks/usePermissions";
import {useAccessDeniedMessage} from "../../../hooks/useAccessDeniedMessage";
import {CREATE_VAT_REPORT} from "../../../constants/eInvoicePermissions";
import PermissionDeniedWrapper from "../../../components/PermissionDeniedWrapper";
import {getVatReportGenerateAccess} from "../utils/getVatReportGenerateAccess";
import {useActionDeniedSnackbar} from "../../../hooks/useActionDeniedSnackbar";

const VATReportPage = () => {
  useDocumentTitle("VAT Returns");
  const { org } = useSelector((/** @type {any} */ state) => state.org);
  const subscription = useSelector(
    (/** @type {any} */ state) => state.subscription.subscription,
  );
  const isVatReportFeatureEnabled = subscription?.features?.enable_vat_report;
  const userEmail = org?.user?.email || "";
  const [state, setState] = useState({
    page: 0,
    size: 10,
    localSuccessSnackbarOpen: false,
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      business_profile: null,
      startDate: null,
      endDate: null,
    },
  });
  const businessProfile = useWatch({ control, name: "business_profile" });
  const startDate = useWatch({ control, name: "startDate" });
  const endDate = useWatch({ control, name: "endDate" });
  const canGenerate =
    !!getProfileVat(businessProfile) && !!startDate && !!endDate;

    const {hasEInvoicePermission} = usePermissions();
    const accessDeniedMessage = useAccessDeniedMessage();
    const rootUserEmail = org?.root_user_email ?? '';
    const hasCreateAccess = hasEInvoicePermission(CREATE_VAT_REPORT);
    const showActionDeniedSnackbar = useActionDeniedSnackbar();
    const { hasAllSalesPurchaseScope, tooltipMessage } = getVatReportGenerateAccess({
        hasEInvoicePermission,
        adminEmail: rootUserEmail,
    });
    const generateTooltip = !hasCreateAccess ? accessDeniedMessage : tooltipMessage;
    const {page, size, localSuccessSnackbarOpen} = state;

  const { reports, totalReports, loading } = useGetDocumentReportsHandler({
    searchQuery: { page, size, type: reportTypes.VAT_REPORT },
  });

  const { onSubmit, isPending } = useCreateReportsJobHandler();
  const handleGenerateReport = (data) => {
      if (!hasEInvoicePermission(CREATE_VAT_REPORT)) {
          showActionDeniedSnackbar();
          return;
      }
    const vatNumber = getProfileVat(data.business_profile);
    if (!vatNumber) {
      setError("business_profile", {
        type: "required",
        message: "Business Profile is required",
      });
    }
    if (!data.startDate) {
      setError("startDate", {
        type: "required",
        message: "Start date is required",
      });
    }
    if (!data.endDate) {
      setError("endDate", {
        type: "required",
        message: "End date is required",
      });
    }
    if (!vatNumber || !data.startDate || !data.endDate) {
      return;
    }
    const createdAfter = data.startDate.startOf("month").format("YYYY-MM-DD");
    const endMonth = data.endDate.startOf("month");
    const createdBefore = endMonth.isSame(dayjs(), "month")
      ? dayjs().format("YYYY-MM-DD")
      : endMonth.endOf("month").format("YYYY-MM-DD");
    const reportData = {
      created_after: createdAfter,
      created_before: createdBefore,
      vat_numbers: [vatNumber],
      type: reportTypes.VAT_REPORT,
    };
    onSubmit(reportData);
    reset();
    setState((prev) => ({ ...prev, localSuccessSnackbarOpen: true }));
  };

  if (!isVatReportFeatureEnabled) {
    return (
      <FeatureNotSupportedDialog
        title="VAT Returns"
        description="Generate VAT return reports for a business profile and tax period based on your e-invoice data."
      />
    );
  }

  return (
    <Stack direction="column" sx={{ mt: "-1rem" }}>
      <TopBar title="VAT Returns">
        <Box>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems="center"
            justifyContent="space-between"
            useFlexGap
            flexWrap="wrap"
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ flex: { md: 1 }, minWidth: 0 }}
            >
              <Box sx={{ minWidth: 240, maxWidth: 320, flex: 1 }}>
                <VatProfileSelect
                  label="VAT No."
                  name="business_profile"
                  control={control}
                  errors={errors.business_profile}
                  size="medium"
                  fullWidth
                />
              </Box>
              <Box sx={{ minWidth: 200, maxWidth: 280 }}>
                <SingleDatePicker
                  control={control}
                  name="startDate"
                  label="Start Month"
                  errors={errors}
                  helperText=""
                  views={["year", "month"]}
                  openTo="month"
                  format="MMM YYYY"
                  minDate={endDate ? dayjs(endDate).subtract(1, "year") : null}
                  maxDate={
                    endDate && dayjs(endDate).isBefore(dayjs())
                      ? dayjs(endDate)
                      : dayjs()
                  }
                />
              </Box>
              <Box sx={{ minWidth: 200, maxWidth: 280 }}>
                <SingleDatePicker
                  control={control}
                  name="endDate"
                  label="End Month"
                  errors={errors}
                  helperText=""
                  views={["year", "month"]}
                  openTo="month"
                  format="MMM YYYY"
                  minDate={startDate || null}
                  maxDate={
                    startDate &&
                    dayjs(startDate).add(1, "year").isBefore(dayjs())
                      ? dayjs(startDate).add(1, "year")
                      : dayjs()
                  }
                />
              </Box>
            </Stack>
              <PermissionDeniedWrapper show={!canGenerate || !hasCreateAccess || !hasAllSalesPurchaseScope} title={generateTooltip}>
              <Button
              id="generate-vat-report"
              variant="contained"
              disabled={!canGenerate || !hasCreateAccess || !hasAllSalesPurchaseScope}
              onClick={handleSubmit(handleGenerateReport)}
              sx={{ minWidth: 160 }}
            >
              {isPending ? "Generating..." : "Generate Report"}
            </Button>
              </PermissionDeniedWrapper>
          </Stack>
        </Box>
      </TopBar>
      <VATReportDisclaimerBanner />
      <ReportTable
        reports={reports}
        loading={loading}
        totalReports={totalReports}
        onPageChange={(newPage) =>
          setState((prev) => ({ ...prev, page: newPage }))
        }
        onPageSizeChange={(newSize) =>
          setState((prev) => ({ ...prev, size: newSize }))
        }
      />
      <Snackbar
        open={localSuccessSnackbarOpen}
        onClose={() =>
          setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))
        }
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() =>
            setState((prev) => ({ ...prev, localSuccessSnackbarOpen: false }))
          }
        >
          Report initiated successfully. We will notify{" "}
          <a
            href={`mailto:${userEmail}`}
            style={{
              fontWeight: "bold",
              color: "inherit",
              textDecoration: "underline",
            }}
          >
            {userEmail}
          </a>{" "}
          once it is ready.
        </Alert>
      </Snackbar>
    </Stack>
  );
};

export default VATReportPage;
